/**
 * src/modules/toast.ts — toast notifications (E.1 strict TS migration)
 *
 * Wzór dla pozostałych modułów: explicit types dla wszystkiego co exposed,
 * `unknown` dla user-supplied input, `declare global` dla window expose.
 */
import { escapeHtml } from "./utils.ts";

export type ToastType = "success" | "error" | "info" | "warning";

const ICON_MAP: Record<ToastType, string> = {
  success: "check-circle",
  error: "alert-circle",
  info: "info",
  warning: "alert-triangle",
};

/**
 * Czas życia toastu (pkt 17, 15.09.2026): 4 s dla krótkich, dłuższe komunikaty do 8 s, błędy min. 6 s —
 * komunikat po akcji ma zniknąć sam, ale zdążyć się przeczytać.
 */
export function toastDuration(message: string, type: ToastType): number {
  const len = String(message || "").length;
  const base = type === "error" ? 6000 : 4000;
  return Math.min(8000, Math.max(base, 4000 + Math.max(0, len - 80) * 40));
}

export function showToast(message: unknown, type?: ToastType, durationMs?: number): void {
  const t: ToastType = type || "success";
  const container = document.getElementById("toast-container");
  if (!container) return;
  if (!container.getAttribute("role")) { container.setAttribute("role", "status"); container.setAttribute("aria-live", "polite"); }

  const toast = document.createElement("div");
  toast.className = "toast";
  toast.innerHTML =
    '<i data-lucide="' + ICON_MAP[t] + '" class="toast-icon ' + t + '"></i>' +
    '<span class="toast-message">' + escapeHtml(message) + "</span>" +
    '<button class="toast-close icon-btn" aria-label="Zamknij">' +
      '<i data-lucide="x" style="width:14px;height:14px"></i>' +
    "</button>";

  container.appendChild(toast);
  // window.lucide jest 3rd-party CDN script — runtime obecność, nie strict-typed
  const lucide = (window as unknown as { lucide?: { createIcons: () => void } }).lucide;
  if (lucide && typeof lucide.createIcons === "function") {
    lucide.createIcons();
  }

  const closeBtn = toast.querySelector<HTMLButtonElement>(".toast-close");
  if (closeBtn) {
    closeBtn.addEventListener("click", function () { removeToast(toast); });
  }

  const life = Number(durationMs) > 0 ? Number(durationMs) : toastDuration(String(message ?? ""), t);
  setTimeout(function () { removeToast(toast); }, life);
}

export function removeToast(toast: HTMLElement | null | undefined): void {
  if (!toast || !toast.parentNode) return;
  toast.classList.add("removing");
  setTimeout(function () {
    if (toast.parentNode) toast.parentNode.removeChild(toast);
  }, 200);
}

/**
 * Faza L.7.e — i18n-aware toast helper.
 * Translates the key via window.__sbI18n.t(key, params) and shows toast.
 * Falls back to raw key gdy i18n niedostępny.
 *
 * Usage:
 *   showToastI18n('toast.order_created', 'success', { id: orderId });
 *   showToastI18n('toast.invoice_saved', 'success');
 */
export function showToastI18n(key: string, type?: ToastType, params?: Record<string, string | number>): void {
  const w = window as unknown as { __sbI18n?: { t: (k: string, p?: Record<string, unknown>) => string } };
  let message: string = key;
  try {
    if (w.__sbI18n && typeof w.__sbI18n.t === "function") {
      message = w.__sbI18n.t(key, params);
    }
  } catch (_e: unknown) {
    // fallback do key
  }
  showToast(message, type);
}

declare global {
  interface Window {
    showToast: typeof showToast;
    removeToast: typeof removeToast;
    showToastI18n: typeof showToastI18n;
  }
}

if (typeof window !== "undefined") {
  Object.assign(window, { showToast, removeToast, showToastI18n });
}
